import { Alert } from '@creditornot/cb-alert';
import { TextButton } from '@creditornot/cb-button';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import React, { FunctionComponent, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';
import { LookerDashboardView } from 'modules/enterprise/components/LookerDashboardView';
import { useHandleLookerEvents } from 'modules/enterprise/hooks/useHandleLookerEvents';

import { useBrandContext } from '../../context/BrandContext';
import useGetBrandAnalyticsDashboards from '../../hooks/useGetBrandAnalyticsDashboards';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const Header = styled.div`
  padding: 32px;
`;

const StyledHeading = styled(T.Heading2)`
  margin-block-end: 8px;
`;

const StyledHr = styled.hr`
  border: none;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
  margin: 0;
`;

const StyledAlert = styled(Alert)`
  border-radius: 0;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  border: none;
  flex: 1 1 auto;
`;

const BrandReporting: FunctionComponent = () => {
  const { brand } = useBrandContext();
  const {
    data: dashboardUrls,
    isLoading,
    error: fetchDashboardsError,
    refetchSoon,
    removeAndRefetch,
  } = useGetBrandAnalyticsDashboards();
  const iframeElArr = useRef<(HTMLIFrameElement | null)[]>([]);
  const { hasLoadDashboardInIframeError } = useHandleLookerEvents(iframeElArr);
  const lookerDashboardViewProps = {
    isLoading,
    hasFetchDashboardError: !!fetchDashboardsError || !dashboardUrls?.length,
    hasLoadDashboardInIframeError,
  };
  return (
    <Root>
      <Header>
        <StyledHeading>{brand?.name}</StyledHeading>
        <T.Body2>
          <FormattedMessage id="looker-analytics.brand" />
        </T.Body2>
      </Header>
      <StyledHr />
      <LookerDashboardView {...lookerDashboardViewProps}>
        {refetchSoon && (
          <StyledAlert variant="warning" size="small">
            <FormattedMessage
              id="looker-analytics.auth-warning"
              values={{
                action: (str: string) => (
                  <TextButton size="inherit" onClick={removeAndRefetch}>
                    {str}
                  </TextButton>
                ),
              }}
            />
          </StyledAlert>
        )}
        {dashboardUrls?.map((url, index) => (
          <StyledIframe
            ref={el => {
              iframeElArr.current[index] = el;
            }}
            key={url}
            src={url}
            data-testid="brand-insight-iframe"
          />
        ))}
      </LookerDashboardView>
    </Root>
  );
};

export default BrandReporting;
